"use client"

import dynamic from "next/dynamic"
import { useEffect, useState } from "react"

import CardsComponent from "@/app/components/ui/Cards/CardsComponent"
import Landing from "@/app/components/ui/Landing"
import LogoComponent from "@/app/components/ui/LogoComponent"
import { SvgLinesComponent } from "@/app/components/ui/SvgLinesComponent"
import { useMobile } from "@/app/lib/hooks/useMobile"
import { paddingClasses } from "@/app/lib/utils/getPaddingClasses"


// Lazy load the 3D component
const ThreeCanvas = dynamic(() => import("./ThreeCanvas"), {
  ssr: false,
  loading: () => <div className="h-full w-full bg-BLACK" />,
})

export default function HeroComponentTwoColumn({ content }: any) {
  const {
    heroComponentTwoColumn: {
      title,
      subtitle,
      ctaButton,
      gradientType,
      showGridLines,
      heroImageDesktop,
      heroImageMobile,
      spacingTop,
      spacingBottom,
      cardsComponent,
      logoComponent,
    },
    sys,
  } = content

  const isMobile = useMobile()
  const [shouldRender3D, setShouldRender3D] = useState(true)

  // Check if device might be low-end
  useEffect(() => {
    // Check for low memory or CPU
    const isLowEndDevice =
      (navigator.deviceMemory && navigator.deviceMemory < 4) ||
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4)

    // Disable 3D on low-end devices
    if (isLowEndDevice) {
      setShouldRender3D(false)
    }
  }, [])

  const paddingTop = paddingClasses.top?.[spacingTop as keyof typeof paddingClasses.top] || "pt-4"
  const paddingBottom = paddingClasses.bottom?.[spacingBottom as keyof typeof paddingClasses.bottom] || "pb-4"

  return (
    <section className="relative">
      <div className={`relative grid h-[82vh] w-full grid-cols-12 ${paddingTop} ${paddingBottom} md-lg:h-full`}>
        {/* Gradient Top */}
        {gradientType === "Purple Gradient" ? (
          <div className="pointer-events-none absolute -z-10 h-full w-screen bg-gradient-top-purple bg-cover bg-top bg-no-repeat" />
        ) : gradientType === "Blue Gradient" ? (
          <div className="pointer-events-none absolute -z-10 h-full w-screen bg-gradient-top-blue bg-cover bg-top bg-no-repeat" />
        ) : null}

        {/* SVG lines */}
        {showGridLines && <SvgLinesComponent />}

        {/* Home Landing */}
        <section className="container z-10 col-span-full grid h-fit grid-cols-12 sm:gap-y-0">
          <Landing
            id={sys.id}
            title={title}
            subtitle={subtitle}
            ctaButton={ctaButton}
            heroImageDesktop={heroImageDesktop}
            heroImageMobile={heroImageMobile}
          />
        </section>
      </div>

      {/* Cards Component */}
      {cardsComponent && (
        <div className="z-20 col-span-full">
          <CardsComponent content={{ cardsComponent }} />
        </div>
      )}

      {/* Logo Component */}
      {logoComponent && (
        <div className="z-20 col-span-full">
          <LogoComponent content={{ logoComponent }} />
        </div>
      )}

      {/* Only render one ThreeCanvas based on device type */}
      {shouldRender3D &&
        (isMobile ? (
          <ThreeCanvas
            className="absolute left-0 top-0 -z-10 h-full mt-10 w-full"
            position={[0, 0.7, 0]}
            scale={1}
          />
        ) : (
          <ThreeCanvas className="absolute left-0 top-0 -z-10 h-full w-full mt-30" position={[0, -0.1, 0]} scale={1.8} />
        ))}
    </section>
  )
}
